import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, Plus, Pencil, ClipboardCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const EMPTY = { title: "", brief: "", objectives: [], rubric: [{ criterion: "", max_score: 25 }], published: true };

export default function LecturerGrading() {
  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["grading-projects"], queryFn: () => api.get("/pjbl/projects").then((r) => r.data) });
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: submissions = [] } = useQuery({
    queryKey: ["submissions", selected?.id],
    queryFn: () => api.get(`/pjbl/submissions?project_id=${selected.id}`).then((r) => r.data),
    enabled: !!selected,
  });

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...p, objectives: p.objectives || [], rubric: p.rubric || [] }); };

  const saveProject = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    const payload = {
      ...form,
      objectives: Array.isArray(form.objectives) ? form.objectives.filter(Boolean) : String(form.objectives).split("\n").filter(Boolean),
      rubric: form.rubric.filter((r) => r.criterion.trim()),
    };
    try {
      if (editing === "new") await api.post("/pjbl/projects", payload);
      else await api.put(`/pjbl/projects/${editing}`, payload);
      toast.success("Proyek disimpan");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["grading-projects"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updRubric = (i, k, v) => setForm((f) => ({ ...f, rubric: f.rubric.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl flex items-center gap-2">
            <GraduationCap className="h-7 w-7 text-primary" /> PjBL & Penilaian
          </h1>
          <p className="text-muted-foreground mt-1">Kelola proyek dan nilai pengumpulan mahasiswa dengan rubrik.</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="new-project-button"><Plus className="h-4 w-4" /> Proyek Baru</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          <h2 className="font-heading font-bold">Proyek</h2>
          {projects.map((p) => (
            <Card key={p.id} className={cn("p-4 rounded-md cursor-pointer transition-colors", selected?.id === p.id ? "border-primary bg-accent/40" : "hover:border-primary/50")} onClick={() => setSelected(p)} data-testid={`grading-project-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-sm">{p.title}</h3>
                <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={(e) => { e.stopPropagation(); openEdit(p); }}><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
              <Badge variant={p.published ? "default" : "outline"} className="mt-2">{p.published ? "Publik" : "Draf"}</Badge>
            </Card>
          ))}
          {projects.length === 0 && <p className="text-sm text-muted-foreground">Belum ada proyek.</p>}
        </div>

        <div className="lg:col-span-2">
          <h2 className="font-heading font-bold mb-3">{selected ? `Submission — ${selected.title}` : "Pilih proyek untuk melihat submission"}</h2>
          <div className="space-y-3">
            {selected && submissions.map((s) => (
              <SubmissionCard key={s.id} submission={s} project={selected} onGraded={() => qc.invalidateQueries({ queryKey: ["submissions", selected.id] })} />
            ))}
            {selected && submissions.length === 0 && <p className="text-sm text-muted-foreground">Belum ada submission untuk proyek ini.</p>}
          </div>
        </div>
      </div>

      {/* Project editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "Proyek Baru" : "Edit Proyek"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Judul</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="project-title-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Deskripsi Masalah</Label><Textarea rows={4} value={form.brief} onChange={(e) => setForm((f) => ({ ...f, brief: e.target.value }))} data-testid="project-brief-input" /></div>
            <div className="space-y-1"><Label className="text-xs">Tujuan (satu per baris)</Label><Textarea rows={3} value={Array.isArray(form.objectives) ? form.objectives.join("\n") : form.objectives} onChange={(e) => setForm((f) => ({ ...f, objectives: e.target.value.split("\n") }))} /></div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs">Rubrik</Label>
                <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, rubric: [...f.rubric, { criterion: "", max_score: 10 }] }))}>+ Kriteria</Button>
              </div>
              <div className="space-y-2">
                {form.rubric.map((r, i) => (
                  <div key={i} className="flex gap-2">
                    <Input className="flex-1" placeholder="Kriteria" value={r.criterion} onChange={(e) => updRubric(i, "criterion", e.target.value)} />
                    <Input type="number" className="w-24 font-data" value={r.max_score} onChange={(e) => updRubric(i, "max_score", parseFloat(e.target.value) || 0)} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => setForm((f) => ({ ...f, published: v }))} /><Label className="text-xs">Publikasikan</Label></div>
          </div>
          <DialogFooter><Button onClick={saveProject} data-testid="save-project-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SubmissionCard({ submission, project, onGraded }) {
  const [open, setOpen] = useState(false);
  const [scores, setScores] = useState(() =>
    (project.rubric || []).map((r) => {
      const existing = submission.grade_scores?.find((g) => g.criterion === r.criterion);
      return { criterion: r.criterion, score: existing ? existing.score : 0, max: r.max_score };
    })
  );
  const [feedback, setFeedback] = useState(submission.feedback || "");

  const submit = async () => {
    try {
      await api.post(`/pjbl/submissions/${submission.id}/grade`, { scores: scores.map((s) => ({ criterion: s.criterion, score: s.score })), feedback });
      toast.success("Nilai tersimpan");
      setOpen(false);
      onGraded();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Card className="p-4 rounded-md" data-testid={`submission-${submission.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{submission.user_name} <span className="font-data text-xs text-muted-foreground">{submission.nim}</span></div>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{submission.student_analysis}</p>
        </div>
        {submission.status === "dinilai"
          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 flex-shrink-0">Nilai {submission.total_score}</Badge>
          : <Badge variant="outline" className="flex-shrink-0">Terkirim</Badge>}
      </div>
      <Button variant="outline" size="sm" className="mt-3 gap-2" onClick={() => setOpen(true)} data-testid={`grade-button-${submission.id}`}>
        <ClipboardCheck className="h-4 w-4" /> {submission.status === "dinilai" ? "Ubah Nilai" : "Beri Nilai"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nilai: {submission.user_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="bg-secondary rounded-md p-3 text-sm">
              <div className="font-semibold text-xs mb-1">Analisis Mahasiswa</div>
              <p className="whitespace-pre-wrap text-muted-foreground">{submission.student_analysis}</p>
            </div>
            {scores.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Label className="text-xs flex-1">{s.criterion} <span className="text-muted-foreground">(maks {s.max})</span></Label>
                <Input type="number" className="w-24 font-data" value={s.score} min={0} max={s.max}
                  onChange={(e) => setScores((arr) => arr.map((x, idx) => (idx === i ? { ...x, score: Math.min(s.max, parseFloat(e.target.value) || 0) } : x)))}
                  data-testid={`score-input-${i}`} />
              </div>
            ))}
            <div className="font-data text-sm text-right">Total: {scores.reduce((a, b) => a + (b.score || 0), 0)}</div>
            <div className="space-y-1"><Label className="text-xs">Umpan balik</Label><Textarea rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid="grade-feedback-input" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="save-grade-button">Simpan Nilai</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
